import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Users, GitMerge, ChevronLeft, Search, Sparkles } from "lucide-react";
import { ContactsApi, SpritesApi } from "../lib/api";
import type { SpriteJob } from "../lib/api";
import { PageHeader } from "../components/PageScaffold";
import { Pill, EmptyState, CenteredSpinner } from "../components/primitives";
import { relativeTime } from "../lib/format";
import { cn } from "../lib/cn";
import type { ContactSummary } from "../lib/types";

function label(name: string | null, fallback: string): string {
  return name && name.trim() ? name : fallback;
}

// Pixel-art visitor sprite for the Visualize office — same panel personas
// get: keyword input, or leave it empty and the fleet's memories about this
// person (preferences, habits, character) are distilled into the design.
// New contacts get one generated automatically.
function ContactSpritePanel({ id, name }: { id: number; name: string }) {
  const qc = useQueryClient();
  const key = `ct-${id}`;
  const [keyword, setKeyword] = useState("");
  const { data: jobs = [] } = useQuery({
    queryKey: ["sprites"],
    queryFn: SpritesApi.list,
    refetchInterval: (q) => {
      const j = (q.state.data as SpriteJob[] | undefined)?.find((r) => r.persona === key);
      return j && (j.status === "describing" || j.status === "generating") ? 2500 : false;
    },
  });
  const job = jobs.find((j) => j.persona === key);
  const busy = (job?.status === "describing" || job?.status === "generating")
    && Date.now() - Date.parse(job.updated_at) < 15 * 60 * 1000;
  const gen = useMutation({
    mutationFn: () => SpritesApi.generateContact(id, keyword.trim() || undefined),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["sprites"] }),
  });
  return (
    <div className="rounded-lg border border-border bg-surface-2/40 p-3 flex items-start gap-3">
      {job?.has_png ? (
        <img
          src={SpritesApi.contactPngUrl(id, job.updated_at)}
          alt={name}
          className="size-16 flex-shrink-0 rounded border border-border bg-surface-elevated"
          style={{ imageRendering: "pixelated" }}
        />
      ) : (
        <div className="size-16 flex-shrink-0 rounded border border-dashed border-border grid place-items-center text-fg-subtle">
          <Users className="size-5" />
        </div>
      )}
      <div className="min-w-0 flex-1 space-y-2">
        <div>
          <div className="text-sm font-medium text-fg">Visitor sprite</div>
          <p className="text-[11px] text-fg-subtle mt-0.5">
            How {name} appears when they walk into the Visualize office. Describe them with a
            keyword — or leave empty and the sprite is designed from what the fleet remembers
            about them (preferences, habits, character).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            disabled={busy}
            className="flex-1 min-w-0 text-sm"
            placeholder="e.g. always in batik, carries a camera (optional)"
          />
          <button
            type="button"
            disabled={busy || gen.isPending}
            onClick={() => gen.mutate()}
            className="h-8 px-3 text-xs rounded bg-surface-2 hover:bg-surface-3 disabled:opacity-50 inline-flex items-center gap-1 flex-shrink-0"
          >
            <Sparkles className="size-3.5" /> {busy ? "drawing…" : job?.has_png ? "Regenerate" : "Generate"}
          </button>
        </div>
        {busy && <p className="text-[11px] text-info">{job?.description ? "drawing the sprite…" : "reading what the fleet remembers…"}</p>}
        {job?.status === "error" && <p className="text-[11px] text-danger break-words">{job.error}</p>}
        {job?.status === "done" && job.description && (
          <p className="text-[11px] text-fg-subtle italic truncate" title={job.description}>“{job.description}”</p>
        )}
      </div>
    </div>
  );
}
const contactLabel = (ct: ContactSummary) => label(ct.display_name, ct.identities[0]?.handle ?? `#${ct.id}`);

// Sort dimensions for the contact list. "recent" preserves the server order
// (contacts come back updated_at DESC — most recently active first).
type SortKey = "recent" | "name" | "chats" | "memories";
const SORT_LABEL: Record<SortKey, string> = {
  recent: "Recent", name: "Name A–Z", chats: "Most chats", memories: "Most remembered",
};

export default function Contacts() {
  const [sel, setSel] = useState<number | null>(null);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState<SortKey>("recent");
  const qc = useQueryClient();
  const { data: list = [], isLoading } = useQuery({
    queryKey: ["contacts"],
    queryFn: ContactsApi.list,
    refetchInterval: 30_000,
  });

  // Client-side filter + sort — the full list is loaded once; searching matches
  // the display name, email, and every identity (handle / name / platform).
  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = !q ? list : list.filter((ct) =>
      (ct.display_name ?? "").toLowerCase().includes(q) ||
      (ct.primary_email ?? "").toLowerCase().includes(q) ||
      ct.identities.some((i) =>
        i.handle.toLowerCase().includes(q) ||
        (i.display_name ?? "").toLowerCase().includes(q) ||
        i.platform.toLowerCase().includes(q)),
    );
    if (sortBy === "recent") return filtered;            // already updated_at DESC from server
    return [...filtered].sort((a, b) => {
      switch (sortBy) {
        case "name": return contactLabel(a).localeCompare(contactLabel(b));
        case "chats": return b.conversations - a.conversations;
        case "memories": return b.memories - a.memories;
        default: return 0;
      }
    });
  }, [list, search, sortBy]);

  const current = sel ?? list[0]?.id ?? null;
  // Mobile single-pane: list until a contact is tapped, then the detail (+Back).
  const detailOpen = sel !== null;
  const { data: detail } = useQuery({
    queryKey: ["contacts", current],
    queryFn: () => ContactsApi.get(current!),
    enabled: current != null,
  });

  const [mergeInto, setMergeInto] = useState<string>("");
  const merge = useMutation({
    mutationFn: () => ContactsApi.merge(current!, Number(mergeInto)),
    onSuccess: () => {
      setMergeInto("");
      setSel(Number(mergeInto));
      qc.invalidateQueries({ queryKey: ["contacts"] });
    },
  });

  const [newName, setNewName] = useState("");
  const setPrimary = useMutation({
    mutationFn: (name: string) => ContactsApi.setPrimaryName(current!, name),
    onSuccess: () => {
      setNewName("");
      qc.invalidateQueries({ queryKey: ["contacts"] });
    },
  });

  if (isLoading) return <CenteredSpinner />;

  return (
    <div className="h-full flex flex-col min-h-0">
      <PageHeader
        title="Contacts"
        icon={Users}
        subtitle={
          list.length
            ? (search.trim() ? `${shown.length} of ${list.length} people` : `${list.length} people across the fleet`)
            : "fleet identity graph"
        }
      />
      <div className="flex-1 min-h-0 panel mx-3 md:mx-5 mb-5 overflow-hidden flex">
        <div className={cn("w-full md:w-64 border-r border-border flex flex-col min-h-0 md:flex-shrink-0", detailOpen ? "hidden md:flex" : "flex")}>
          <div className="border-b border-border px-2 py-2 space-y-1.5 flex-shrink-0">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-fg-subtle pointer-events-none" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="search name · handle · email…"
                className="w-full pl-8 h-7 text-xs"
              />
            </div>
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value as SortKey)} className="h-7 text-xs w-full" title="Sort contacts">
              {(Object.keys(SORT_LABEL) as SortKey[]).map((k) => <option key={k} value={k}>↕ {SORT_LABEL[k]}</option>)}
            </select>
          </div>
          <div className="flex-1 overflow-y-auto min-h-0">
            {shown.map((ct) => (
              <button
                key={ct.id}
                onClick={() => setSel(ct.id)}
                className={cn("w-full text-left px-3 py-2 border-b border-border hover:bg-surface-2 transition-colors", current === ct.id && "bg-surface-2")}
              >
                <div className="text-sm font-medium text-fg truncate">{contactLabel(ct)}</div>
                <div className="text-[10px] text-fg-subtle font-mono">
                  {ct.identities.length} id{ct.identities.length !== 1 ? "s" : ""} · {ct.conversations} chat · {ct.memories} mem
                </div>
              </button>
            ))}
            {list.length === 0 && <div className="p-4 text-xs text-fg-subtle">No contacts yet — they register automatically as people message the fleet.</div>}
            {list.length > 0 && shown.length === 0 && <div className="p-4 text-xs text-fg-subtle">No contacts match “{search}”.</div>}
          </div>
        </div>

        <div className={cn("flex-1 min-w-0 min-h-0 flex-col", detailOpen ? "flex" : "hidden md:flex")}>
          {detailOpen && (
            <button
              type="button"
              onClick={() => setSel(null)}
              className="md:hidden flex items-center gap-1 px-3 py-2.5 border-b border-border text-sm text-fg-muted hover:text-fg bg-surface flex-shrink-0"
            >
              <ChevronLeft className="size-4" /> Contacts
            </button>
          )}
          <div className="flex-1 overflow-y-auto min-h-0">
          {!detail && <div className="p-6"><EmptyState icon={<Users className="size-7" />} title="Select a contact" /></div>}
          {detail && (
            <div className="p-5 space-y-5">
              <div>
                <div className="text-lg font-semibold text-fg">{label(detail.display_name, `Contact #${detail.id}`)}</div>
                {detail.primary_email && <div className="text-xs text-fg-subtle">{detail.primary_email}</div>}
              </div>

              <ContactSpritePanel id={detail.id} name={label(detail.display_name, `Contact #${detail.id}`)} />

              <section>
                <div className="text-[11px] uppercase tracking-wide text-fg-subtle mb-1.5">Names</div>
                <div className="space-y-1">
                  {detail.names.map((n) => (
                    <div key={n} className="flex items-center gap-2 text-sm">
                      <span className="text-fg">{n}</span>
                      {n === detail.display_name?.trim() ? (
                        <Pill tone="info">primary</Pill>
                      ) : (
                        <button
                          disabled={setPrimary.isPending}
                          onClick={() => setPrimary.mutate(n)}
                          className="text-xs text-fg-muted hover:text-fg underline disabled:opacity-40"
                        >
                          make primary
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                <form
                  className="flex items-center gap-2 mt-2"
                  onSubmit={(e) => { e.preventDefault(); if (newName.trim()) setPrimary.mutate(newName); }}
                >
                  <input
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    placeholder="or type a new primary name…"
                    className="h-7 text-xs flex-1 max-w-xs"
                  />
                  <button
                    type="submit"
                    disabled={!newName.trim() || setPrimary.isPending}
                    className="h-7 px-3 text-xs rounded bg-surface-2 hover:bg-surface-3 disabled:opacity-40"
                  >
                    Set primary
                  </button>
                </form>
                <div className="text-[10px] text-fg-subtle mt-1">Every name stays on the list after a merge or a rename.</div>
              </section>

              <section>
                <div className="text-[11px] uppercase tracking-wide text-fg-subtle mb-1.5">Identities</div>
                <div className="space-y-1">
                  {detail.identities.map((i, n) => (
                    <div key={n} className="flex items-center gap-2 text-sm">
                      <Pill tone="info">{i.platform}</Pill>
                      <span className="font-mono text-xs text-fg-muted">{i.handle}</span>
                      {i.display_name && <span className="text-xs text-fg-subtle">({i.display_name})</span>}
                    </div>
                  ))}
                </div>
              </section>

              {detail.conversations.length > 0 && (
                <section>
                  <div className="text-[11px] uppercase tracking-wide text-fg-subtle mb-1.5">Conversations</div>
                  <div className="space-y-1">
                    {detail.conversations.map((cv) => (
                      <div key={cv.chat_id} className="flex items-center gap-2 text-sm">
                        <Pill tone="neutral">{cv.kind ?? "?"}</Pill>
                        <span className="text-fg">{cv.title ?? cv.chat_id}</span>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              <section>
                <div className="text-[11px] uppercase tracking-wide text-fg-subtle mb-1.5">Dossier — what agents remember</div>
                {detail.dossier.length === 0 && <div className="text-xs text-fg-subtle">No agent has memories about this person yet.</div>}
                <div className="space-y-3">
                  {detail.dossier.map((d) => (
                    <div key={d.agent}>
                      <div className="text-xs font-medium text-fg-muted mb-1">{d.agent}</div>
                      <div className="space-y-1 pl-2 border-l-2 border-border">
                        {d.memories.map((m) => (
                          <div key={m.id} className="text-sm text-fg">
                            <span className="text-fg-subtle text-[10px] font-mono mr-1">{m.tier}</span>
                            {m.subject && <span className="text-[11px] font-mono text-fg-subtle">[{m.subject}] </span>}
                            {m.content}
                            <span className="text-[10px] text-fg-subtle ml-1">· {relativeTime(m.created_at)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              <section className="pt-2 border-t border-border">
                <div className="text-[11px] uppercase tracking-wide text-fg-subtle mb-1.5 flex items-center gap-1"><GitMerge className="size-3" /> Merge</div>
                <div className="flex items-center gap-2">
                  <select value={mergeInto} onChange={(e) => setMergeInto(e.target.value)} className="h-7 text-xs flex-1 max-w-xs">
                    <option value="">merge this contact into…</option>
                    {list.filter((ct) => ct.id !== detail.id)
                      .sort((a, b) => contactLabel(a).localeCompare(contactLabel(b)))
                      .map((ct) => (
                        <option key={ct.id} value={ct.id}>{contactLabel(ct)}</option>
                      ))}
                  </select>
                  <button
                    disabled={!mergeInto || merge.isPending}
                    onClick={() => merge.mutate()}
                    className="h-7 px-3 text-xs rounded bg-surface-2 hover:bg-surface-3 disabled:opacity-40"
                  >
                    {merge.isPending ? "merging…" : "Merge"}
                  </button>
                </div>
                <div className="text-[10px] text-fg-subtle mt-1">Non-destructive — identities move to the target; this contact becomes a redirect.</div>
              </section>
            </div>
          )}
          </div>
        </div>
      </div>
    </div>
  );
}
